import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import type { RingPayload } from '@/redux/app/actions/calls';
import { pressable, useDraggable } from './use-draggable';

// Mirrors the legacy #incoming-call-prompt (app/views/shared/_incoming_call_prompt.html.erb,
// styled in app/assets/stylesheets/layouts/_shared.scss) so both call flows look the same
const Prompt = styled.div`
  background: white;
  padding: 10px;
  max-width: 350px;
  text-align: center;
  border-radius: 8px;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
`;

const CalleePhone = styled.div`
  font-size: 22px;
  margin-bottom: 10px;
  max-width: 250px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const Buttons = styled.div`
  margin: 15px 0;
`;

// &&& outranks jQuery Mobile's `html body .ui-group-theme-a a` link color
const PromptButton = styled.a<{ color: string }>`
  cursor: pointer;
  background-color: ${({ $color }) => $color};
  color: white;
  padding: 10px 15px;
  border-radius: 8px;
  font-size: 20px;
  font-family: Montserrat, sans-serif;

  &&&, &&&:hover, &&&:focus {
    color: white;
    text-decoration: none;
  }

  &[aria-disabled='true'] {
    cursor: default;
  }
`;

export interface IncomingCallPromptProps {
  ring: RingPayload;
  claiming: boolean;
  onAnswer: () => void;
  onDecline: () => void;
  // Called once the ring window has closed without an answer or a server stop
  onExpire?: () => void;
}

// The server broadcasts call_ring_stop when the ring window closes; give that
// event a moment to arrive before the prompt retires itself.
export const EXPIRY_GRACE_MS = 2000;

const remainingSeconds = (ring: RingPayload) => Math.max(
  0,
  Math.ceil(ring.started_at + ring.timeout_seconds - Date.now() / 1000),
);

export const IncomingCallPrompt = ({
  ring, claiming, onAnswer, onDecline, onExpire,
}: IncomingCallPromptProps) => {
  const [remaining, setRemaining] = useState(() => remainingSeconds(ring));
  const draggable = useDraggable();
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    const interval = setInterval(() => setRemaining(remainingSeconds(ring)), 1000);
    return () => clearInterval(interval);
  }, [ring]);

  // Safety net for a missed call_ring_stop (websocket hiccup, or the prompt was
  // restored from the store after the window closed): never show a stale ring.
  // A claim in flight resolves the prompt on its own, so leave it alone then.
  useEffect(() => {
    if (remaining > 0 || claiming) return;
    const timeout = setTimeout(() => onExpireRef.current?.(), EXPIRY_GRACE_MS);
    return () => clearTimeout(timeout);
  }, [remaining, claiming]);

  // Legacy anchors can't be disabled, so ignore clicks while a claim is in flight
  const guard = (handler: () => void) => () => {
    if (!claiming) handler();
  };

  return (
    <Prompt role="alertdialog" aria-label="Incoming call" {...draggable}>
      Receiving a Call From
      <CalleePhone>{ring.contact_name || ring.contact_phone}</CalleePhone>
      <Buttons>
        <PromptButton aria-disabled={claiming} $color="#00B879" {...pressable(guard(onAnswer))}>
          {claiming ? 'Connecting…' : 'Accept'}
        </PromptButton>
        {' '}
        <PromptButton aria-disabled={claiming} $color="#d22600" {...pressable(guard(onDecline))}>
          Ignore
        </PromptButton>
      </Buttons>
    </Prompt>
  );
};
